/** 料号分支格子：9 行 × 10 列（A–J），与桌面期初/库存分支明细一致 */

export const BRANCH_GRID_ROWS = 9
export const BRANCH_GRID_COLS = 10

const EPS = 1e-6

export function sumBranchDetails (branchDetails) {
  if (!branchDetails || !Array.isArray(branchDetails) || branchDetails.length === 0) return 0
  const first = branchDetails[0]
  if (first && typeof first === 'object' && !Array.isArray(first) && ('quantity' in first || 'row' in first)) {
    return branchDetails.reduce((s, item) => s + (parseFloat(item.quantity) || 0), 0)
  }
  let total = 0
  branchDetails.forEach((rowData) => {
    if (Array.isArray(rowData)) {
      rowData.forEach((cell) => {
        total += parseFloat(cell) || 0
      })
    }
  })
  return total
}

/** 单元格展示/录入：有数则最多一位小数，无则空串（不显示一堆 0） */
export function normalizeCellInputString (raw) {
  if (raw == null) return ''
  const t = String(raw).trim().replace(/,/g, '')
  if (t === '') return ''
  const n = parseFloat(t)
  if (!Number.isFinite(n) || n < 0) return ''
  if (n < EPS) return ''
  const r = Math.round(n * 10) / 10
  if (r === 0) return ''
  if (Number.isInteger(r)) return String(r)
  const s = r.toFixed(1)
  return s.endsWith('.0') ? String(Math.round(r)) : s
}

export function branchDetailsToGridStrings (branchDetails) {
  const grid = Array.from({ length: BRANCH_GRID_ROWS }, () =>
    Array(BRANCH_GRID_COLS).fill('')
  )
  if (!branchDetails || !Array.isArray(branchDetails)) return grid
  const first = branchDetails[0]
  if (first && typeof first === 'object' && !Array.isArray(first) && ('quantity' in first || 'row' in first)) {
    branchDetails.forEach((item) => {
      const r = (item.row || item.row_index || 1) - 1
      let c = 0
      if (item.col && typeof item.col === 'string') {
        c = item.col.toUpperCase().charCodeAt(0) - 65
      } else {
        c = Number(item.col_index) || 0
      }
      if (r >= 0 && r < BRANCH_GRID_ROWS && c >= 0 && c < BRANCH_GRID_COLS) {
        grid[r][c] = normalizeCellInputString(item.quantity)
      }
    })
    return grid
  }
  branchDetails.forEach((rowData, ri) => {
    if (!Array.isArray(rowData)) return
    rowData.forEach((cell, ci) => {
      if (ri < BRANCH_GRID_ROWS && ci < BRANCH_GRID_COLS) {
        grid[ri][ci] = normalizeCellInputString(cell)
      }
    })
  })
  return grid
}

/** 保存为 [{ row, col, quantity }] 与桌面 InitialStockDialog 一致 */
export function gridStringsToBranchDetailsPayload (grid) {
  const out = []
  for (let r = 0; r < BRANCH_GRID_ROWS; r++) {
    for (let c = 0; c < BRANCH_GRID_COLS; c++) {
      const cell = grid[r]?.[c]
      const s = normalizeCellInputString(cell)
      if (s === '') continue
      const q = parseFloat(s)
      if (!Number.isFinite(q) || q <= 0) continue
      const rounded = Math.round(q * 10) / 10
      out.push({
        row: r + 1,
        col: String.fromCharCode(65 + c),
        quantity: Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)
      })
    }
  }
  return out.length ? out : null
}

export function cellLabel (rowIndex, colIndex) {
  return `${rowIndex + 1}${String.fromCharCode(65 + colIndex)}`
}

export function occupiedBranchCells (branchDetails) {
  const grid = branchDetailsToGridStrings(branchDetails)
  const list = []
  for (let r = 0; r < BRANCH_GRID_ROWS; r++) {
    for (let c = 0; c < BRANCH_GRID_COLS; c++) {
      const s = grid[r][c]
      if (s === '') continue
      const q = parseFloat(s)
      if (!Number.isFinite(q) || q < EPS) continue
      list.push({ row: r, col: c, label: cellLabel(r, c), qty: q })
    }
  }
  return list
}

/** 仅非空格：按数量升序，同量按格位标签排序（便于「几支显几格」） */
export function occupiedBranchCellsSortedAsc (branchDetails) {
  const list = occupiedBranchCells(branchDetails)
  list.sort((a, b) => {
    if (Math.abs(a.qty - b.qty) > EPS) return a.qty - b.qty
    return String(a.label).localeCompare(String(b.label), 'zh-CN', { numeric: true, sensitivity: 'base' })
  })
  return list
}

/** 分支合计与账面比较时的容差（码） */
export const BRANCH_QTY_EPS = 0.01

function roundBranchQty (q) {
  const n = parseFloat(q)
  if (!Number.isFinite(n)) return null
  return Math.round(n * 10) / 10
}

function formatBranchQtyShort (q) {
  const r = roundBranchQty(q)
  if (r == null) return ''
  if (Number.isInteger(r)) return String(r)
  const s = r.toFixed(1)
  return s.endsWith('.0') ? String(Math.round(r)) : s
}

/** 按码数分组 [{ quantity, count }]，数量升序（不展示格位支号） */
export function branchQuantityGroupsFromDetails (branchDetails) {
  const cells = occupiedBranchCells(branchDetails)
  const map = new Map()
  for (const c of cells) {
    const q = roundBranchQty(c.qty)
    if (q == null || q < EPS) continue
    map.set(q, (map.get(q) || 0) + 1)
  }
  return [...map.entries()]
    .map(([quantity, count]) => ({ quantity, count }))
    .sort((a, b) => a.quantity - b.quantity)
}

/** 从后端 branch_details_lines（含 label）按 quantity 分组 */
export function branchQuantityGroupsFromLines (lines) {
  if (!Array.isArray(lines) || !lines.length) return []
  const map = new Map()
  for (const it of lines) {
    const q = roundBranchQty(it?.quantity)
    if (q == null || q < EPS) continue
    map.set(q, (map.get(q) || 0) + 1)
  }
  return [...map.entries()]
    .map(([quantity, count]) => ({ quantity, count }))
    .sort((a, b) => a.quantity - b.quantity)
}

/** 「50码，150码×3」 */
export function formatBranchQuantityGroups (groups) {
  if (!Array.isArray(groups) || !groups.length) return ''
  return groups
    .map((g) => {
      const qs = formatBranchQtyShort(g.quantity)
      if (!qs) return ''
      const cnt = Number(g.count) || 0
      return cnt > 1 ? `${qs}码×${cnt}` : `${qs}码`
    })
    .filter(Boolean)
    .join('，')
}

/**
 * 入库通知 / 流水详情：分组展示分支，附带总支数。
 * 优先 branch_details_groups，兼容旧 payload 的 lines / 原始 branch_details。
 */
export function formatInboundBranchSummary (source = {}) {
  const groups =
    (Array.isArray(source.branch_details_groups) && source.branch_details_groups.length
      ? source.branch_details_groups
      : null) ||
    branchQuantityGroupsFromLines(source.branch_details_lines) ||
    branchQuantityGroupsFromDetails(source.slot_branch_details) ||
    branchQuantityGroupsFromDetails(source.branch_details)

  const grouped = formatBranchQuantityGroups(groups)
  if (grouped) {
    const total = groups.reduce((s, g) => s + (Number(g.count) || 0), 0)
    return total > 0 ? `${grouped}（共${total}支）` : grouped
  }
  const legacy = String(source.branch_details_display || '').trim()
  return legacy
}

/**
 * 列表/详情行上用于展示的分支明细（优先本库位 slot_branch_details / 已合并的 branch_details，否则料号全局）
 */
export function mergedBranchDetailsForDisplay (row) {
  if (!row) return null
  if (row.slot_branch_details != null) return row.slot_branch_details
  return row.branch_details ?? row.material_batch?.branch_details ?? null
}

/**
 * 编辑「本库位」格子时的初始 payload：仅用 slot_branch_details；无则空表新录（不与其它库位共用）
 */
export function slotBranchDetailsForEdit (row) {
  if (!row || !Object.prototype.hasOwnProperty.call(row, 'slot_branch_details')) {
    return null
  }
  return row.slot_branch_details
}

/** 是否有可展示的分支格子数量（与库存列表展开区一致） */
export function hasBranchDetailsPayload (branchDetails) {
  if (!branchDetails || !Array.isArray(branchDetails) || branchDetails.length === 0) return false
  return sumBranchDetails(branchDetails) > 0.01
}

export function allBranchCellsForPan (branchDetails) {
  const grid = branchDetailsToGridStrings(branchDetails)
  const list = []
  for (let r = 0; r < BRANCH_GRID_ROWS; r++) {
    for (let c = 0; c < BRANCH_GRID_COLS; c++) {
      const s = grid[r][c]
      const q = s === '' ? null : parseFloat(s)
      list.push({
        row: r,
        col: c,
        label: cellLabel(r, c),
        qty: q != null && Number.isFinite(q) && q >= EPS ? q : null
      })
    }
  }
  return list
}
